/**
 * Auth Callback Page
 *
 * Handles Supabase auth redirects (email verification, password recovery, etc.).
 * When using the PKCE flow, Supabase redirects here with a `code` query param
 * that must be exchanged for a session before navigating elsewhere.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const AuthCallback = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleAuth = async () => {
      const url = new URL(window.location.href);
      const code = url.searchParams.get("code");

      if (code) {
        // PKCE flow: exchange the code for a session
        const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
        if (exchangeError) {
          console.error("[AuthCallback] Code exchange failed:", exchangeError.message);
          setError(exchangeError.message);
          // Redirect to login after a short delay so the user can see the error
          setTimeout(() => navigate("/login", { replace: true }), 3000);
          return;
        }
      }

      // After successful exchange (or if tokens were in the hash),
      // check if we have a session and redirect accordingly.
      const { data: { session } } = await supabase.auth.getSession();

      if (session) {
        // Check if user is admin or account manager — they should never be
        // redirected to onboarding even if their agent record is incomplete.
        const { data: adminRow } = await supabase
          .from("admin_users")
          .select("role")
          .eq("user_id", session.user.id)
          .maybeSingle();

        const { data: amRow } = await supabase
          .from("account_managers")
          .select("id")
          .eq("user_id", session.user.id)
          .maybeSingle();

        if (adminRow) {
          // Admin/sales users go straight to admin dashboard
          navigate("/admin", { replace: true });
          return;
        }

        if (amRow) {
          // Account managers go straight to AM dashboard
          navigate("/am", { replace: true });
          return;
        }

        // Regular agents: check if onboarding is needed
        const { data: agent } = await supabase
          .from("agents")
          .select("licensed_states")
          .eq("id", session.user.id)
          .maybeSingle();

        const hasOnboarded = agent?.licensed_states && (agent.licensed_states as string[]).length > 0;
        const hasSignupData = !!localStorage.getItem("tj_signup");

        if (!hasOnboarded && hasSignupData) {
          navigate("/onboarding", { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      } else {
        // No session — redirect to login
        navigate("/login", { replace: true });
      }
    };

    handleAuth();
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-2">
          <p className="text-destructive font-medium">Verification failed</p>
          <p className="text-sm text-muted-foreground">{error}</p>
          <p className="text-xs text-muted-foreground">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-2">
        <div className="animate-pulse text-muted-foreground">Verifying your account...</div>
      </div>
    </div>
  );
};

export default AuthCallback;
